import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import { togglePopup } from 'apps/promo/actions/appAction';
import { DiskButton } from 'apps/promo/components/common/DiskButton/DiskButton';
import type { AppDispatch } from 'apps/promo/redux/store';

import { PopupBoxClose, PopupButtons, PopupContent, PopupHeader } from './styles';
import { Text } from '../Text/Text';

type PopupRefHandle = { close: () => void };

interface SimplePopupProps {
    text?: React.ReactNode;
    withoutCloseButton?: boolean;
    withoutBorder?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    popupRef?: React.RefObject<PopupRefHandle>;
    handleClose?: () => void;
    dataTestId?: string;
    onPopupClose?: () => void;
    children?: React.ReactNode;
}

type PopupButtonsProps = React.ComponentProps<typeof PopupButtons>;
const StyledPopupButtons = PopupButtons as React.ComponentType<PopupButtonsProps & { withoutBorder?: boolean }>;

const SimplePopup = ({ popupRef, ...props }: SimplePopupProps) => {
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        return () => {
            props.onPopupClose?.();
        };
    }, [props]);

    const handleButtonClick = () => {
        if (props.handleClose) {
            props.handleClose();
            return;
        }
        if (popupRef?.current) {
            popupRef.current.close();
            return;
        }
        void dispatch(togglePopup(false));
    };

    return (
        <div data-testid={props.dataTestId}>
            {!props.withoutCloseButton && (
                <PopupBoxClose>
                    <DiskButton
                        onClick={handleButtonClick}
                        iconName="cross"
                        iconColor="mediumGray"
                        size="small"
                        type="button"
                        variant="tertiary"
                        raised={false}
                    />
                </PopupBoxClose>
            )}
            <PopupContent>
                {props.text && (
                    <PopupHeader>
                        <Text variant="div" textAlign={props.textAlign}>
                            {props.text}
                        </Text>
                    </PopupHeader>
                )}
                <StyledPopupButtons withoutBorder={props.withoutBorder}>{props.children}</StyledPopupButtons>
            </PopupContent>
        </div>
    );
};

SimplePopup.propTypes = {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    withoutCloseButton: PropTypes.bool,
    withoutBorder: PropTypes.bool,
    textAlign: PropTypes.oneOf(['left', 'center', 'right']),
    popupRef: PropTypes.shape({ current: PropTypes.shape({ close: PropTypes.func }) }),
    handleClose: PropTypes.func,
    dataTestId: PropTypes.string,
    onPopupClose: PropTypes.func,
};

SimplePopup.displayName = 'SimplePopup';

export default SimplePopup;
